/**
 * ASN delivery-line Excel import modal (CR-4888 Slice D).
 *
 * Supplier downloads a blank template, populates it from their ERP, uploads,
 * backend creates a synthetic call-off + delivery lines, returns the new
 * delivery_line_ids. Caller wires those IDs into the wizard's selection.
 */
import { useState, useRef } from 'react';
import { Loader2, Download, Upload, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { purchasingAPI } from '../../../lib/api';
import { toast } from 'sonner';

interface ImportResult {
  total: number;
  imported: number;
  ignored: number;
  errors: number;
  error_details: Array<{ row: number; errors: string[] }>;
  delivery_line_ids: number[];
}

interface ImportASNLinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: (deliveryLineIds: number[]) => void;
}

export function ImportASNLinesModal({ isOpen, onClose, onImported }: ImportASNLinesModalProps) {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setSelectedFile(null);
    setResult(null);
    setErrorMsg(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await purchasingAPI.downloadASNTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asn_delivery_lines_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await purchasingAPI.importASNLines(formData);
      const data = response.data;
      setResult(data);
      if (data.imported > 0) {
        toast.success(
          `${data.imported} delivery line${data.imported !== 1 ? 's' : ''} imported`
        );
      } else {
        toast.info('No delivery lines imported');
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: string; detail?: string } };
      };
      const msg =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Upload failed.');
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleApplyAndClose = () => {
    if (result && result.delivery_line_ids.length > 0) {
      onImported(result.delivery_line_ids);
    }
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Delivery Lines from Excel</DialogTitle>
          <DialogDescription>
            Download the template, populate it from your ERP, then upload to create
            delivery lines for this ASN.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md bg-muted/50 border border-border px-4 py-3 text-sm space-y-1">
            <p>
              <span className="font-medium">Step 1:</span> Download the template — fill the
              <span className="font-semibold"> Customer Name</span>,
              <span className="font-semibold"> Part Number</span>, and
              <span className="font-semibold"> Quantity</span> columns at minimum.
            </p>
            <p>
              <span className="font-medium">Step 2:</span> Add optional
              <span className="font-semibold"> PO Number</span>,
              <span className="font-semibold"> Part Description</span>,
              <span className="font-semibold"> Delivery Number</span>, and
              <span className="font-semibold"> Notes</span> per row.
            </p>
            <p>
              <span className="font-medium">Step 3:</span> Upload — the system creates one
              delivery line per valid row and returns them so you can attach them to this ASN.
            </p>
            <p className="text-muted-foreground mt-1">
              Customer Name must match an existing customer in the system. Quantity must be
              a positive number. Rows missing required fields are skipped.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="flex items-center gap-2"
            >
              {downloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Download Template
            </Button>

            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Choose File
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFile(file);
                  setResult(null);
                  setErrorMsg(null);
                }
              }}
            />
          </div>

          {selectedFile && !result && (
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
              <span className="font-medium truncate">{selectedFile.name}</span>
              <div className="flex items-center gap-2 ml-2 shrink-0">
                <Button size="sm" onClick={handleUpload} disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      Uploading…
                    </>
                  ) : (
                    'Import'
                  )}
                </Button>
                <button
                  onClick={reset}
                  className="p-0.5 hover:bg-muted rounded"
                  aria-label="Clear file"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {errorMsg}
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-semibold">Import complete</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-2 font-medium">Total</th>
                    <th className="text-left px-4 py-2 font-medium">Imported</th>
                    <th className="text-left px-4 py-2 font-medium">Ignored</th>
                    {result.errors > 0 && (
                      <th className="text-left px-4 py-2 font-medium text-red-600">Errors</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3">{result.total}</td>
                    <td className="px-4 py-3 text-green-700 font-medium">{result.imported}</td>
                    <td className="px-4 py-3 text-muted-foreground">{result.ignored}</td>
                    {result.errors > 0 && (
                      <td className="px-4 py-3 text-red-600 font-medium">{result.errors}</td>
                    )}
                  </tr>
                </tbody>
              </table>
              {result.error_details.length > 0 && (
                <div className="px-4 py-2 border-t border-border">
                  <div className="text-xs font-medium text-red-700 mb-1">Skipped rows:</div>
                  <ul className="text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                    {result.error_details.map((e) => (
                      <li key={e.row}>
                        Row {e.row}: {e.errors.join('; ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
                <Button variant="outline" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleApplyAndClose}
                  disabled={result.imported === 0}
                >
                  Add to ASN
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
